import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { HlmIconComponent } from '@spartan-ng/ui-icon-helm';

import { SanitizeHtmlPipe } from 'src/app/@shared/pipes/sanitizeHtml.pipe';
import { mergeClasses } from 'src/app/@core/utils/merge-classes';
import {
  IconSource,
  IconSourceEnum,
  IconSize,
  IconSizeEnum,
  IconVariant,
  IconVariantEnum,
  VARIANT_COLOR_MAP,
  PRIMENG_SIZE_MAP,
  ICON_SIZE_CLASS_MAP,
} from './icon.types';

export type { IconSource, IconSize, IconVariant };
export { IconSourceEnum, IconSizeEnum, IconVariantEnum };

// kebab-case → lucidePascalCase (e.g. 'circle-check-big' → 'lucideCircleCheckBig')
function kebabToLucideName(name: string): string {
  return 'lucide' + name.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
}

/**
 * Universal icon wrapper component (`app-icon` / `z-icon`).
 *
 * Icon appearance is controlled exclusively through `size`, `variant`, and `source`.
 * The `type` input is always a **string**:
 *   - Lucide: kebab-case key, e.g. `'chevron-right'`
 *   - PrimeNG: CSS class string, e.g. `'pi pi-home'`
 *   - SVG: raw SVG HTML string
 *
 * - **lucide** (default): Renders via @spartan-ng/ui-icon-helm + @ng-icons/lucide.
 * - **primeng**: Renders `<i class="pi pi-...">`.
 * - **svg**: Renders raw SVG HTML via sanitized `innerHTML`.
 */
@Component({
  selector: 'z-icon, app-icon',
  standalone: true,
  imports: [HlmIconComponent, SanitizeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  template: `
    @switch (source()) {

      @case (ICON_SOURCE.LUCIDE) {
        <!--
          size="none" keeps hlm-icon from adding its own sizing variant.
          hlm-icon reads h-N w-N from [class] via MutationObserver when size="none".
          We pass both size and color here so the inner SVG fills the right dimensions.
        -->
        <hlm-icon
          [name]="ngIconName()"
          size="none"
          [strokeWidth]="strokeWidth()"
          [class]="hlmIconClasses()"
        />
      }

      @case (ICON_SOURCE.PRIMENG) {
        <i [class]="primeNGClasses()"></i>
      }

      @case (ICON_SOURCE.SVG) {
        <i [innerHTML]="uniqueSvg() | sanitizeHtml" [class]="svgClasses()"></i>
      }

    }
  `,
  host: {
    '[class]': 'hostClasses()',
  },
})
export class IconComponent {
  readonly type = input.required<string>();
  readonly source = input<IconSource>(IconSourceEnum.LUCIDE);
  readonly size = input<IconSize | null | undefined>(IconSizeEnum.SM);
  readonly variant = input<IconVariant | string>(IconVariantEnum.DEFAULT);
  readonly strokeWidth = input<number>(1.5);

  protected readonly ICON_SOURCE = IconSourceEnum;

  private static _instanceCounter = 0;
  private readonly _instanceId = ++IconComponent._instanceCounter;

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  /** Converts kebab-case Lucide key to ng-icons name, e.g. `'circle-check-big'` → `'lucideCircleCheckBig'`. */
  protected readonly ngIconName = computed<string>(() => kebabToLucideName(this.type()));

  /** Resolved Tailwind colour class from variant */
  protected readonly variantClass = computed(() => {
    const v = this.variant();
    if (!v || v === IconVariantEnum.DEFAULT) return '';
    return VARIANT_COLOR_MAP[v] ?? '';
  });

  /**
   * Classes passed to hlm-icon:
   *   - h-N w-N for the icon size (read by hlm-icon's MutationObserver to size the inner SVG)
   *   - colour class from variant
   */
  protected readonly hlmIconClasses = computed(() => {
    const sizeClass = ICON_SIZE_CLASS_MAP[this.size() ?? IconSizeEnum.DEFAULT] ?? 'h-5 w-5';
    return mergeClasses('items-center justify-center', sizeClass, this.variantClass());
  });

  /** Host classes: sized by the `size` input + variant colour */
  protected readonly hostClasses = computed(() => {
    const sizeClass = ICON_SIZE_CLASS_MAP[this.size() ?? IconSizeEnum.DEFAULT] ?? 'h-5 w-5';
    return mergeClasses('flex items-center justify-center shrink-0', sizeClass, this.variantClass());
  });

  // ---------------------------------------------------------------------------
  // PrimeNG rendering
  // ---------------------------------------------------------------------------
  protected readonly primeNGClasses = computed(() => {
    const sizeClass = PRIMENG_SIZE_MAP[this.size() ?? IconSizeEnum.DEFAULT] ?? 'icon-default';
    return [this.type(), sizeClass, this.variantClass()].filter(Boolean).join(' ');
  });

  // ---------------------------------------------------------------------------
  // SVG rendering
  // ---------------------------------------------------------------------------

  /**
   * Returns the SVG string with all `id="..."` attributes and their `url(#...)`
   * references rewritten to be unique per component instance. This prevents
   * duplicate-ID conflicts (e.g. gradient IDs) when the same SVG icon is
   * injected more than once on the same page.
   */
  protected readonly uniqueSvg = computed(() => {
    let svg = this.type();
    const idMap = new Map<string, string>();
    svg = svg.replace(/\bid="([^"]+)"/g, (_match, id: string) => {
      const uniqueId = `${id}_i${this._instanceId}`;
      idMap.set(id, uniqueId);
      return `id="${uniqueId}"`;
    });
    idMap.forEach((uniqueId, id) => {
      svg = svg.replace(new RegExp(`url\\(#${id}\\)`, 'g'), `url(#${uniqueId})`);
    });
    return svg;
  });

  protected readonly svgClasses = computed(() =>
    mergeClasses('flex items-center justify-center h-full w-full [&_svg]:w-full [&_svg]:h-full', this.variantClass())
  );
}
